import { Button, Checkbox, FormControl, FormControlLabel, FormGroup, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { ContextMenu, ContextMenuOptions } from "../base/ContextMenu";
import { assertThumbnailUrl, LoadState } from "../../redux/common";
import { UserInfo } from "../../redux/user/userSlice";
import { useSelector } from "react-redux";
import { executeUserPermissionInsDel, selectUserPermissionInsDel } from "../../redux/user/userPermissionInsDelSlice";
import { getPermission, hasDirectPermission, Permission, PermissionId } from "../../app/permissions";
import { useAppDispatch } from "../../app/hooks";
import BadgedAvatar from "../base/BadgedAvatar";

export interface UserPermissionsCardProps {
    targetId: string;
    permissions: PermissionId[];
    user: UserInfo;
}

const UserPermissionsCard = (props: UserPermissionsCardProps) => {
    const dispatch = useAppDispatch();
    const userPermissionInsDel = useSelector(selectUserPermissionInsDel);

    const user = props.user;

    const name = user.name || user.email || "Desconhecido";

    const contextMenuLabel = <BadgedAvatar
        key={user.email}
        title={name}
        avatar={`${assertThumbnailUrl(user.url)}`}
        size='80px'
        // click={() => { /* context menu will do the job, don't trigger any action from the avatar itself */ }}
    />;

    const permButtons = [] as React.ReactNode[];
    const permOptions = [] as React.ReactNode[];

    props.permissions.forEach((permissionId) => {
        const permission: Permission = getPermission(permissionId);
        const has = hasDirectPermission(user, permissionId, props.targetId);
        const handleChange = (event: any) => {
            event.preventDefault();
            dispatch(executeUserPermissionInsDel({
                userId: user.uid,
                permissionId: permission.id,
                targetId: props.targetId,
                action: has ? "del" : "ins",
            }));
        };

        // permission button
        permButtons.push(
            <Tooltip title={
                <Paper elevation={3} sx={{ p: 1 }}>
                    <Stack spacing={1} direction="column" alignItems="center">
                        <Typography variant="body2">
                            {has ? "Permitido (Clique para revogar)" : "Não permitido (Clique para permitir)"}
                        </Typography>
                        <Typography variant="h6">
                            {permission.name}
                        </Typography>
                        <Typography variant="caption">
                            {permission.description}
                        </Typography>
                    </Stack>
                </Paper>
            } key={permission.id}>
                <Button size='small' sx={{ minWidth: 32, color: has ? "green" : "gray" }} onClick={handleChange} key={permission.id}>
                    {permission.icon}
                </Button>
            </Tooltip>
        );

        // permission for the context menu
        permOptions.push(
            <FormControlLabel 
                key={permission.id} control={<Checkbox checked={has} 
                onChange={handleChange} name={permission.name} />} label={permission.name} />
        );
    });

    const formPermissions =
        <FormControl disabled={userPermissionInsDel.state.state == LoadState.Loading} component="fieldset" sx={{ m: 1 }}>
            <FormGroup>
                {permOptions}
            </FormGroup>
        </FormControl>;
    const contextMenuOptions: ContextMenuOptions[] = [
        { label: formPermissions, close: false, menuItem: false }
    ];

    return <Paper sx={{ m: 1, p: 2 }} key={name} elevation={5}>
        <Stack key={user.email} spacing={0} direction="row" justifyContent="center" alignItems="center">
            <ContextMenu sx={{ width: "100%", p: 0 }}
                key={user.email} label={contextMenuLabel} title={name} options={contextMenuOptions || []} />
            <Stack direction="column" justifyContent="left" alignItems="left" sx={{ flexGrow: 1 }}>
                <Typography variant="h6" component="span" 
                    sx={{ width: "200px", overflow: "hidden", textOverflow: "ellipsis", 
                    display: "block", textWrap: "nowrap", textAlign: "left" }}>
                    {name}
                </Typography>
                <Stack spacing={1} direction="row" justifyContent="left" alignItems="center">
                    {permButtons}
                </Stack>
            </Stack>
        </Stack>
    </Paper>;
};

export default UserPermissionsCard;